
import { Component, Inject, Vue, Emit, Ref, Prop, Watch } from "vue-property-decorator";
import { Container } from "inversify";
import { Conference } from "@gsx/common";
import { Coordinates } from "@gsx/common";
import { ConferenceListStore } from "../../stores/conference/ConferenceListStore";
import { ConferenceFilterStore } from "../../stores/conference/ConferenceFilterStore";
import { handleError } from "../../../../common/src/utility/errorUtils";
import { debounce } from "debounce";

@Component({
    components: {},
})
export default class EventMap extends Vue {
    @Inject("container") container!: Container;
    @Ref("map") readonly map!: HTMLDivElement;
    @Prop() currentMonth!: number;

    readonly listStore: ConferenceListStore = this.container.get("ConferenceMapListStore");
    readonly filterStore: ConferenceFilterStore = this.container.get(ConferenceFilterStore);

    showCurrentMonth = false;

    created() {
        this.listStore.afterDate = new Date();
        this.listStore.pageSize = Number.MAX_SAFE_INTEGER;
        this.listStore.load().catch(handleError);
    }

    mounted() {
        this.reload();

        // @ts-ignore
        $(this.map).vectorMap({
            map: "world_mill_en",
            normalizeFunction: "polynomial",
            zoomMax: 200,
            zoomStep: 2,
            zoomAnimate: false,
            hoverOpacity: 0.7,
            hoverColor: false,
            regionStyle: {
                initial: {
                    fill: "#e3eaef",
                },
                hover: {
                    fill: "#e69334",
                },
            },
            markerStyle: {
                initial: {
                    r: 9,
                    fill: "#727cf5",
                    "fill-opacity": 0.9,
                    stroke: "#fff",
                    "stroke-width": 7,
                    "stroke-opacity": 0.4,
                },
                hover: {
                    fill: "#e69334",
                    r: 8,
                    stroke: "#fff",
                    "fill-opacity": 1,
                    "stroke-width": 1.5,
                },
                selected: {
                    r: 15,
                    fill: "#e69334",
                    stroke: "#fff",
                },
                selectedHover: {
                    fill: "#e69334",
                    r: 8,
                    stroke: "#fff",
                    "fill-opacity": 1,
                    "stroke-width": 1.5,
                },
            },
            backgroundColor: "transparent",
            onMarkerClick: (_e: any, code: string) => {
                this.handleEventClick(code);
            },
        });

        this.updateMapMarkers();
    }

    get events() {
        return this.listStore.items || [];
    }

    get monthName() {
        const formatter = new Intl.DateTimeFormat("en", { month: "long" });

        const date = new Date().setMonth(this.currentMonth);
        const month = formatter.format(date);

        return month;
    }

    beforeDestroy() {
        this.getMapObject().remove();
    }

    @Watch("currentMonth")
    @Watch("showCurrentMonth")
    @Watch("events")
    updateMapMarkers() {
        let mapObject = this.getMapObject();

        if (!mapObject) {
            return;
        }

        mapObject.removeAllMarkers();

        let eventsToShow = this.events;
        if (this.showCurrentMonth) {
            eventsToShow = eventsToShow.filter((event) => {
                return event.startDate.getMonth() === this.currentMonth;
            });
        }

        eventsToShow.forEach((event) => {
            if (event.coordinates && event.coordinates.latitude && event.coordinates.longitude) {
                mapObject.addMarker(
                    `${event.conferenceid}`,
                    this.createLocation(event, event.coordinates, "black"),
                );
            }
        });
    }

    createLocation(
        conference: Conference,
        coordinates: Coordinates | null | undefined,
        color: string,
    ) {
        return {
            latLng: [coordinates!.latitude, coordinates!.longitude],
            name: conference.name,
            style: {
                r: 8,
                fill: color,
            },
        };
    }

    @Emit("eventClick")
    handleEventClick(eventId: any) {
        return this.listStore.items?.find((conference) => conference.conferenceid === eventId);
    }

    getMapObject() {
        return $(this.map).find(".jvectormap-container").data("mapObject");
    }

    @Watch("listStore.query")
    @Watch("filterStore.topics")
    @Watch("filterStore.types")
    @Watch("filterStore.countries")
    @Watch("filterStore.cities")
    @Watch("filterStore.logicalOperator")
    reloadDebounce = debounce(this.reload, 300);

    reload() {
        this.listStore.reload().catch(handleError);
    }
}
